import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, AlertCircle, Loader2, X, CheckCircle2, UploadCloud, Sparkles } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

const formatBytes = (bytes) => {
  if (!bytes) return '0 MB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUpload = ({ onUploadSuccess }) => {

  const [isUploading, setIsUploading] = useState(false);

  const [uploadResult, setUploadResult] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);

  const fileInputRef = useRef(null);
  const totalFileSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);



  const handleFileChange = (e) => {

    const files = Array.from(e.target.files || []);

    if (!files.length) return;



    setSelectedFiles((prev) => {

      const existingKeys = new Set(prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`));

      const deduped = files.filter((file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`));

      return [...prev, ...deduped];

    });

    setUploadResult(null);

    if (fileInputRef.current) {

      fileInputRef.current.value = '';

    }

  };



  const handleRemoveFile = (index) => {

    setSelectedFiles((prev) => {

      const updated = prev.filter((_, idx) => idx !== index);

      if (updated.length === 0 && fileInputRef.current) {

        fileInputRef.current.value = '';

      }

      return updated;

    });

  };



  const handleUpload = async () => {

    if (!selectedFiles.length) return;



    setIsUploading(true);

    setUploadResult(null);



    const formData = new FormData();

    selectedFiles.forEach((file) => formData.append('files', file));



    try {

      const res = await axios.post('/api/upload', formData, {

        headers: {

          'Content-Type': 'multipart/form-data',

        },

      });

      setUploadResult({ type: 'success', data: res.data });

      onUploadSuccess(); // Trigger data refresh in dashboard

      setSelectedFiles([]);

      if (fileInputRef.current) {

        fileInputRef.current.value = '';

      }

    } catch (err) {

      console.error("Upload error:", err);

      const message = err.response?.data?.error || 'Upload failed. See console for details.';

      setUploadResult({ type: 'error', message });

    } finally {

      setIsUploading(false);

    }

  };
  const renderUploadResult = () => {
    if (!uploadResult) {
      return (
        <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center text-sm text-slate-500">
          <Sparkles className="mb-3 text-brand-500" size={28} />
          <p>Processing summary will land here after your next upload.</p>
        </div>
      );
    }

    if (uploadResult.type === 'error') {
      return (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/80 p-5 text-sm text-rose-800">
          <p className="font-semibold">Upload failed</p>
          <p className="mt-1">{uploadResult.message}</p>
        </div>
      );
    }

    const { data } = uploadResult;
    const { status, summary, files, aiInsights } = data;
    const badgeClasses = {
      success: 'bg-emerald-500/20 text-emerald-900',
      partial: 'bg-amber-500/20 text-amber-900',
      error: 'bg-rose-500/20 text-rose-900',
    };
    const badgeText = {
      success: 'Success',
      partial: 'Partial Success',
      error: 'Failed',
    };
    const statusCopy = {
      success: 'All files processed successfully.',
      partial: 'Some files completed with warnings or errors.',
      error: 'We could not process any of the files.',
    };

    return (
      <div className="space-y-5">
        <div className="rounded-3xl border border-white/40 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-6 text-white shadow-panel">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-white/60">Upload report</p>
              <h4 className="text-2xl font-semibold">Status: {badgeText[status] || status}</h4>
              <p className="text-sm text-white/70">{statusCopy[status]}</p>
            </div>
            <span className={`inline-flex items-center rounded-full px-4 py-1 text-xs font-semibold ${badgeClasses[status] || 'bg-white/20 text-white'}`}>
              {badgeText[status] || status}
            </span>
          </div>
          <dl className="mt-6 grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <div className="rounded-2xl border border-white/20 bg-white/5 p-4">
              <dt className="text-xs uppercase tracking-[0.35em] text-white/70">Files processed</dt>
              <dd className="mt-2 text-3xl font-semibold">{summary.filesProcessed}</dd>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/5 p-4">
              <dt className="text-xs uppercase tracking-[0.35em] text-white/70">New transactions</dt>
              <dd className="mt-2 text-3xl font-semibold">{summary.totalAdded}</dd>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/5 p-4">
              <dt className="text-xs uppercase tracking-[0.35em] text-white/70">Duplicates skipped</dt>
              <dd className="mt-2 text-3xl font-semibold">{summary.totalSkipped}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white/80 p-4 text-sm shadow-inner">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">File</th>
                <th className="px-3 py-2 text-left">Bank</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Added</th>
                <th className="px-3 py-2 text-right">Duplicates</th>
                <th className="px-3 py-2 text-left">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {files.map((file, idx) => (
                <tr key={`${file.fileName}-${idx}`}>
                  <td className="px-3 py-2 font-semibold text-slate-900">{file.fileName}</td>
                  <td className="px-3 py-2">{file.bank || 'Unknown'}</td>
                  <td className="px-3 py-2 capitalize">{file.status}</td>
                  <td className="px-3 py-2 text-right">{file.added ?? '-'}</td>
                  <td className="px-3 py-2 text-right">{file.skipped ?? '-'}</td>
                  <td className="px-3 py-2 text-slate-500">{file.message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <UploadAiInsights insights={aiInsights} />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">Ingest</p>
            <h2 className="text-3xl font-semibold text-slate-900">Upload new statements</h2>
            <p className="text-sm text-slate-500">
              Drop CSV exports from Chase, Citi, Amex, Wells Fargo, or drag multiple files in one go. We’ll dedupe automatically.
            </p>
          </div>

          <label className="mt-6 flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-10 text-center text-slate-500 transition hover:border-brand-400 hover:bg-white">
            <UploadCloud size={32} className="text-brand-500" />
            <div>
              <p className="text-base font-semibold text-slate-900">
                {selectedFiles.length ? `${selectedFiles.length} file(s) selected` : 'Select or drop .CSV file(s)'}
              </p>
              <p className="text-xs text-slate-500">We ignore duplicates, so you can re-upload safely.</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              accept=".csv"
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </label>

          {selectedFiles.length > 0 && (
            <div className="mt-6 rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-inner">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">Selected files</h3>
                <button
                  onClick={() => {
                    setSelectedFiles([]);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                >
                  Clear all
                </button>
              </div>
              <ul className="mt-3 space-y-3 text-sm text-slate-600">
                {selectedFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${file.lastModified}`}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-2"
                  >
                    <div>
                      <p className="font-semibold text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500">{formatBytes(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="rounded-full bg-white/70 p-1.5 text-slate-500 hover:text-rose-500"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
            <p>
              {selectedFiles.length} file(s) · {formatBytes(totalFileSize)}
            </p>
            <button
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-500 via-fuchsia-500 to-sky-400 px-6 py-3 text-sm font-semibold text-white shadow-glow transition disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
            >
              {isUploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
              {isUploading ? 'Uploading...' : 'Upload & Process'}
            </button>
          </div>
        </section>

        <section className="flex flex-col gap-5 rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel">
          <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-amber-900">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5" size={20} />
              <div>
                <h3 className="text-sm font-semibold">PDFs aren’t supported</h3>
                <p className="text-xs">
                  Please export a <strong>.CSV</strong> statement from your bank. Drag it here once it’s downloaded.
                </p>
              </div>
            </div>
          </div>

          {renderUploadResult()}
        </section>
      </div>
    </div>
  );
};



const UploadAiInsights = ({ insights }) => {

  if (!insights) return null;

  const { narrative, anomalies = [], qaFindings } = insights;

  if (!narrative && (!anomalies || anomalies.length === 0) && !qaFindings) {

    return null;

  }



  return (
    <div className="rounded-2xl border border-white/30 bg-white/95 p-5 shadow-panel">
      <div className="flex items-center gap-2 text-brand-600">
        <Sparkles size={18} />
        <p className="text-sm font-semibold uppercase tracking-wide">AI insights</p>
      </div>
      {narrative && <p className="mt-3 text-slate-700">{narrative}</p>}
      {anomalies && anomalies.length > 0 && (
        <div className="mt-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-600">
            <AlertCircle size={14} className="text-rose-500" />
            <span>Potential anomalies</span>
          </p>
          <div className="mt-2 space-y-2">
            {anomalies.map((item, idx) => (
              <div key={`${item.description}-${idx}`} className="rounded-xl border border-rose-100 bg-rose-50/60 p-3">
                <div className="flex items-center justify-between text-sm font-medium text-slate-900">
                  <span>{item.description}</span>
                  {Number.isFinite(item.amount) && <span>{formatCurrency(item.amount, 2)}</span>}
                </div>
                <p className="mt-1 text-xs text-slate-500">{[item.date, item.bank].filter(Boolean).join(' - ')}</p>
                <p className="mt-1 text-sm text-slate-600">{item.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {qaFindings && (
        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-sm text-slate-600">
          <p className="font-semibold text-slate-900">QA notes</p>
          <p className="mt-1">{qaFindings}</p>
        </div>
      )}
    </div>
  );

};

export default FileUpload;
